// React port of android/.../ui/MainViewModel.kt. Same state shape, same
// transitions, same timing constants — kept 1:1 so behavior parity between
// the two players doesn't drift. When fixing a bug here, check the Android
// VM and apply the same fix there (and vice versa).
//
// Lifecycle on mount:
//   1. Read prefs. If a device_token exists, paint cached content + fetch fresh.
//      Otherwise auto-register and start fetching.
//   2. On every fetch:
//        404           → clear prefs, set screenRemoved=true (UI shows
//                        ScreenRemovedScreen)
//        !is_paired    → start 10s pairing poll
//        !is_active    → start 30s deactivated poll
//        else          → connect WebSocket + 30s refresh fallback
//   3. New content arriving over WS / refresh is *staged* into pendingContent
//      and only swapped in when ContentDisplay calls applyPendingContent() at
//      the next item boundary, so a fresh content_update never interrupts the
//      middle of a video.
//   4. Heartbeat fires every 30s once registered.

import { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import {
  ApiError,
  BASE_URL,
  deviceApi,
  type ContentResponse,
} from '../lib/deviceApi'
import { deviceInfo } from '../lib/deviceInfo'
import { warmVideoCache } from '../lib/videoCache'
import { DISCONNECT_CODES, codeForReason } from '../lib/disconnectCodes'
import {
  clearCachedContent,
  clearCredentials,
  loadCachedContent,
  loadCountdownHours,
  loadCountdownLastWall,
  loadDeviceUid,
  loadLastFetchedAt,
  loadRegisterRetryAt,
  loadScreenId,
  loadToken,
  saveCachedContent,
  saveCountdownHours,
  saveCountdownLastWall,
  saveCredentials,
  saveLicenseState,
  saveRegisterRetryAt,
  clearRegisterRetryAt,
} from '../lib/devicePrefs'

// A layout publishes content across zones, leaving the flat `items` array
// empty — so "does this response have anything to render?" must check both
// the flat items and the layout's zone items. Used by the cache-restore /
// TTL / silent-swap gates that previously keyed off items.length alone, which
// read a zoned layout as "no content" and dropped it.
function hasRenderableContent(c: ContentResponse | null | undefined): boolean {
  if (!c) return false
  if (c.items.length > 0) return true
  return c.layout?.zones.some(z => z.items.length > 0) ?? false
}

// Battery Status API (Chromium-only; Firefox/Safari removed it). The manager
// object is live — level/charging update in place — so we acquire it once and
// read fresh values on every heartbeat. Null where the API doesn't exist.
type BatteryManagerLike = { level: number; charging: boolean }
let batteryManagerPromise: Promise<BatteryManagerLike | null> | null = null
function getBatteryManager(): Promise<BatteryManagerLike | null> {
  if (!batteryManagerPromise) {
    const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> }
    batteryManagerPromise = typeof nav.getBattery === 'function'
      ? nav.getBattery().catch(() => null)
      : Promise.resolve(null)
  }
  return batteryManagerPromise
}

// ── Timing constants — match android/.../MainViewModel.kt ──────────────────
const POLL_PAIRING_MS    = 10_000
const POLL_DEACTIVATED_MS = 30_000
const REFRESH_INTERVAL_MS = 30_000
const WS_RECONNECT_MS     = 10_000
const HEARTBEAT_INTERVAL_MS = 30_000
// How long the player keeps replaying cached content after the last
// successful /content fetch when the screen has no per-row override
// (Screen.cache_ttl_hours). Default 168h = 7 days; tunable per screen
// from the dashboard.
const CACHE_TTL_DEFAULT_HOURS = 168
// How often to evaluate the TTL — cheap (just a date comparison) so
// running it every minute is fine and keeps the offline screen
// appearing within a minute of the TTL boundary.
const CACHE_TTL_CHECK_MS = 60_000
// How often to tick the monotonic embargo countdown. Same cadence as the
// TTL loop — by design, both expire the cache "within a minute" of their
// real deadline. The loop reads countdown hours from localStorage and
// decrements by (monotonic delta since last tick / 3.6M). See the
// clock-tampering write-up in the plan file for what this defends.
const COUNTDOWN_TICK_MS = 60_000
const REGISTER_RETRY_DEFAULT_MS = 60_000
const REGISTER_RETRY_MAX_MS = 5 * 60_000

// ── State shape — mirrors ScreenState in MainViewModel.kt ─────────────────
export interface PlayerState {
  registered:     boolean
  deviceToken:    string
  screenId:       number
  // Operator-facing identity. Surfaced in the drawer INSTEAD of screenId (which
  // is sensitive and never shown). screenName is set once paired; branchName is
  // null when the screen isn't assigned to a branch.
  screenName:     string
  branchName:     string
  isPaired:       boolean
  isActive:       boolean
  pairingCode:    string | null
  content:        ContentResponse | null
  pendingContent: ContentResponse | null  // staged; applied at item boundary
  // Two-axis orientation. Held at top-level so it can update independently
  // of the staged content (the rotation should follow the operator's edit
  // immediately, even if the visual carousel is still on the previous
  // playlist's item-boundary).
  orientationType: 'horizontal' | 'vertical'
  rotationDegrees: 0 | 90 | 180 | 270
  error:          string | null
  noInternet:     boolean
  isOnline:       boolean | null          // null = first fetch not done yet
  loading:        boolean
  serverUrl:      string
  screenRemoved:  boolean                 // 404 from /content → screen was deleted
  // Diagnostic code for the last disconnect (license expiry / removal / relink),
  // shown on the SetupScreen so the operator can read it to their Account
  // Manager. null when the screen wasn't disconnected (fresh boot / normal pair).
  disconnectCode: number | null
  // Set true the instant a `content_preparing` WS event arrives (dashboard
  // operator hit Publish). Stays true through the follow-up `content_update`
  // fetch and is cleared when the new content swaps in. App renders the
  // PublishingLoader over the current content while this is true so the
  // operator sees instant feedback instead of a multi-second black screen.
  publishing:     boolean
  // True when cached content has been evicted (TTL exceeded OR admin hit
  // Evict). The App renders CacheExpiredScreen instead of ContentDisplay
  // until the next successful /content fetch refreshes both `content` and
  // clears this flag. License expiry no longer routes through this flag —
  // it unpairs the player (handleOrphanedAndReregister) instead.
  cacheExpired:   boolean
  // Epoch-ms of the last successful /content fetch. Drives the TTL check
  // and is the basis for comparing against cache_evict_at from /content
  // responses (if evict_at > lastFetchedAt, the cache is evicted).
  lastFetchedAt:  number | null
  pingOverlay:    { screenId: number; name: string } | null  // null = hidden
}

const INITIAL: PlayerState = {
  registered:     false,
  deviceToken:    '',
  screenId:       -1,
  screenName:     '',
  branchName:     '',
  isPaired:       false,
  isActive:       true,
  pairingCode:    null,
  content:        null,
  pendingContent: null,
  orientationType: 'horizontal',
  rotationDegrees: 0,
  error:          null,
  noInternet:     false,
  isOnline:       null,
  loading:        true,
  serverUrl:      BASE_URL,
  screenRemoved:  false,
  publishing:     false,
  cacheExpired:   false,
  lastFetchedAt:  null,
  pingOverlay:    null,
  disconnectCode: null,
}

export interface UsePlayerState {
  state:                PlayerState
  fetchContent:         () => void
  applyPendingContent:  () => void
  reportError:          (msg: string | null) => void
  reportRetry:          (itemName: string | null) => void
}

export function usePlayerState(): UsePlayerState {
  const [state, setState] = useState<PlayerState>(INITIAL)

  // Mirror the latest state into a ref so timers / WS callbacks can read it
  // without re-subscribing every render (mirrors Kotlin's _state.value access).
  const stateRef = useRef(state)
  stateRef.current = state

  // Timers + WS handles, all cleared on unmount.
  const wsRef                   = useRef<WebSocket | null>(null)
  const wsReconnectTimerRef     = useRef<number | null>(null)
  const pairingPollTimerRef     = useRef<number | null>(null)
  const deactivatedPollTimerRef = useRef<number | null>(null)
  const refreshIntervalRef      = useRef<number | null>(null)
  const heartbeatIntervalRef    = useRef<number | null>(null)
  // RTT of the last completed heartbeat — reported on the NEXT one (see
  // startHeartbeatLoop). null until the first heartbeat round-trips.
  const heartbeatRttMsRef       = useRef<number | null>(null)
  const ttlCheckIntervalRef     = useRef<number | null>(null)
  const countdownIntervalRef    = useRef<number | null>(null)
  // Monotonic reference for the embargo countdown. performance.now() is
  // immune to wall-clock changes but resets on tab reload — the loop falls
  // through to wall-clock-forward-only when this ref is null on a tick.
  const countdownMonoRef        = useRef<number | null>(null)

  // StrictMode in dev mounts every effect twice. autoRegister creates a row
  // on the server, so a double-fire would spawn two Screen entities. This
  // guard makes the boot sequence run exactly once per real mount.
  const didBootRef = useRef(false)
  const registerInFlightRef = useRef(false)
  const registerRetryTimerRef = useRef<number | null>(null)
  const registerBackoffMsRef = useRef(REGISTER_RETRY_DEFAULT_MS)

  // ── Helpers ──────────────────────────────────────────────────────────────

  const clearTimer = (ref: React.MutableRefObject<number | null>) => {
    if (ref.current !== null) {
      window.clearTimeout(ref.current)
      window.clearInterval(ref.current)
      ref.current = null
    }
  }

  const isOnlineHint = () => (typeof navigator !== 'undefined' ? navigator.onLine : true)

  const recordPlayerApiError = (token: string, err: unknown, context: string) => {
    const status = err instanceof ApiError ? `HTTP ${err.status}` : 'network'
    const message = err instanceof Error ? err.message : String(err)
    deviceApi.reportError(token, `Player API error (${context}): ${status} ${message}`)
      .catch(() => { /* best-effort internal admin log */ })
  }

  // Compare server-side cache_evict_at against our locally-stored "last
  // fetched at". If the marker is newer, the operator pressed Force
  // Disconnect while we couldn't be reached — we evict our cache. We use
  // the prefs value (not stateRef.lastFetchedAt) because this can fire
  // during the very first fetch after a cold boot, before state is set.
  const wasEvictedSinceLastFetch = (evictAtIso?: string | null): boolean => {
    if (!evictAtIso) return false
    const evictAt = Date.parse(evictAtIso)
    if (!Number.isFinite(evictAt)) return false
    const lastFetched = loadLastFetchedAt()
    if (lastFetched === null) return false   // never fetched before — nothing to compare
    return evictAt > lastFetched
  }

  // Compute the effective cache TTL for the current screen. Per-screen
  // override (Screen.cache_ttl_hours) wins; falls back to the player
  // default. Returns ms for easy comparison.
  const effectiveCacheTtlMs = (content: ContentResponse | null): number => {
    const hours = content?.cache_ttl_hours ?? CACHE_TTL_DEFAULT_HOURS
    return hours * 3_600_000
  }

  // ── License countdown helpers ──────────────────────────────────────────
  // Re-seed countdown state from a fresh server response (heartbeat /
  // content / silent-refresh / license_renewed WS event). Single call
  // site so we never drift.
  const seedLicenseFromResponse = (resp: {
    disconnect_at?: string | null
    hours_until_disconnect?: number | null
  }): void => {
    saveLicenseState({
      disconnectAt:         resp.disconnect_at      ?? null,
      hoursUntilDisconnect: resp.hours_until_disconnect ?? null,
    })
    // Reset the monotonic anchor so the next tick decrements from "now"
    // forward, not from a stale pre-renew baseline.
    countdownMonoRef.current = performance.now()
  }

  // License-expiry self-unpair — the player just discovered offline that
  // its countdown ran out. Re-route through the existing orphan flow so
  // the next register call lands us on the same Screen row (via
  // device_uid) and we get a fresh pairing code without a stuck UI.
  // Defined as a function declaration so it can forward-reference
  // handleOrphanedAndReregister below.
  function selfUnpairForLicenseExpiry() {
    handleOrphanedAndReregister(DISCONNECT_CODES.LICENSE_EXPIRED)
  }

  // Tick the monotonic countdown. In-session uses performance.now()
  // (immune to clock changes); across reload uses the wall-clock delta
  // but only credits forward movement. Persists every tick so a reboot
  // still picks up where the last running session left off.
  const tickLicenseCountdown = (): void => {
    const remaining = loadCountdownHours()
    if (remaining === null) return  // perpetual / no expiry set
    if (!stateRef.current.isPaired) return  // already unpaired — nothing to count down to
    const wallNow  = Date.now()
    const monoNow  = performance.now()
    const monoLast = countdownMonoRef.current
    const wallLast = loadCountdownLastWall()
    let deltaMs: number
    if (monoLast !== null) {
      // In-session: trust the monotonic delta. Cannot be rolled back.
      deltaMs = Math.max(0, monoNow - monoLast)
    } else {
      // First tick after reload — fall back to wall-clock but credit
      // forward movement only. wallLast missing means we have nothing
      // to compare against; credit zero (effectively pausing) so a
      // first-boot countdown isn't double-charged.
      deltaMs = wallLast !== null ? Math.max(0, wallNow - wallLast) : 0
    }
    const next = remaining - deltaMs / 3_600_000
    saveCountdownHours(next)
    saveCountdownLastWall(wallNow)
    countdownMonoRef.current = monoNow
    if (next <= 0) {
      selfUnpairForLicenseExpiry()
    }
  }

  const disconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close() } catch { /* ignore */ }
      wsRef.current = null
    }
    clearTimer(wsReconnectTimerRef)
  }, [])

  const scheduleWsReconnect = useCallback(() => {
    if (wsReconnectTimerRef.current !== null) return
    wsReconnectTimerRef.current = window.setTimeout(() => {
      wsReconnectTimerRef.current = null
      const s = stateRef.current
      if (s.isPaired && s.isActive && !s.screenRemoved) connectWebSocket()
    }, WS_RECONNECT_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Called whenever the server reports 404 for our device_token: the Screen
  // has been deleted OR (more commonly) the dashboard relinked it to a
  // different device. We can't recover with this token, so wipe local state
  // and auto-register so the operator sees a fresh pairing code immediately
  // — no reload, no dead "screen removed" page.
  //
  // Defined as a function declaration so the useCallback'd
  // fetchContentImpl / fetchContentSilently (declared below) can forward-
  // reference it via closure hoisting.
  function handleOrphanedAndReregister(disconnectCode: number | null = null) {
    clearCredentials()
    disconnectWebSocket()
    clearTimer(pairingPollTimerRef)
    clearTimer(deactivatedPollTimerRef)
    clearTimer(refreshIntervalRef)
    setState(prev => ({
      ...prev,
      registered:     false,
      deviceToken:    '',
      screenId:       -1,
      screenName:     '',
      branchName:     '',
      isPaired:       false,
      isActive:       true,
      pairingCode:    null,
      content:        null,
      pendingContent: null,
      error:          null,
      loading:        true,
      screenRemoved:  false,
      cacheExpired:   false,
      lastFetchedAt:  null,
      disconnectCode,
    }))
    countdownMonoRef.current = null
    scheduleAutoRegister()
  }

  function scheduleAutoRegister(delayMs?: number) {
    if (registerRetryTimerRef.current !== null) return
    const persistedRetryAt = loadRegisterRetryAt()
    const persistedDelay = persistedRetryAt !== null ? Math.max(0, persistedRetryAt - Date.now()) : 0
    const wait = Math.max(delayMs ?? 0, persistedDelay)
    if (wait <= 0) {
      void autoRegister()
      return
    }
    registerRetryTimerRef.current = window.setTimeout(() => {
      registerRetryTimerRef.current = null
      void autoRegister()
    }, wait)
  }

  // ── Fetch content (full) ─────────────────────────────────────────────────

  const fetchContentImpl = useCallback(async (token: string) => {
    try {
      const content = await deviceApi.getContent(token)
      // Compare cache_evict_at to our locally-stored last fetch — if the
      // operator hit Force Disconnect while we were offline, the server
      // returns a marker newer than our last successful fetch. Clear the
      // localStorage cache (but keep creds + the new content we just got)
      // so a subsequent reload doesn't paint stale frames.
      const evictedExternally = wasEvictedSinceLastFetch(content.cache_evict_at)
      if (evictedExternally) {
        clearCachedContent()
      }
      saveCachedContent(content)
      warmVideoCache(content)
      void deviceApi.reportError(token, null).catch(() => { /* best-effort recovery signal */ })
      // Re-seed the offline license countdown from the server's authoritative
      // hours_until_disconnect. Each successful response is the player's
      // "renew" — an admin extending the license sends a bigger countdown.
      seedLicenseFromResponse(content)
      const fetchedAt = Date.now()
      const identityChanged = stateRef.current.screenId > 0
        && stateRef.current.screenId !== content.screen_id
      setState(prev => ({
        ...prev,
        screenId:    content.screen_id,        // may shift on relink — see WS handler
        screenName:  content.screen_name ?? '',
        branchName:  content.branch_name ?? '',
        content:     content.is_paired ? content : null,
        isPaired:    content.is_paired,
        isActive:    content.is_active,
        pairingCode: content.pairing_code ?? null,
        orientationType: content.orientation_type,
        rotationDegrees: content.rotation_degrees,
        error:       null,
        isOnline:    true,
        loading:     false,
        cacheExpired: false,    // fresh content = TTL clock reset
        lastFetchedAt: fetchedAt,
      }))
      if (identityChanged) disconnectWebSocket()
      // Connect WS regardless of paired/active state so a relink push (which
      // targets the *unpaired* device's screen_id) arrives instantly instead
      // of waiting for the next 10s pairing-poll tick.
      connectWebSocket()
      if (!content.is_paired)      startPairingPoll()
      else if (!content.is_active) startDeactivatedPoll()
      else                         startRefreshLoop()
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // License-expiry 404s carry X-Disconnect-Code (→ err.disconnectCode);
        // a bare removal/relink 404 has none, so show the generic "removed" code.
        const disconnectCode = err.disconnectCode ?? DISCONNECT_CODES.REMOVED
        // Orphaned device: this player's token doesn't map to any Screen on
        // the server. Three converging causes — admin delete, dashboard
        // relink, or license expiry (server-side unpair via the heartbeat /
        // content path). All resolve through the same auto-register so we
        // show a fresh pairing code instead of a dead "Screen removed" page.
        handleOrphanedAndReregister(disconnectCode)
        return
      }
      const offline = !isOnlineHint()
      const hadContent = stateRef.current.content !== null
      if (hadContent) {
        recordPlayerApiError(token, err, 'content refresh')
        setState(prev => ({ ...prev, isOnline: false, noInternet: offline, loading: false }))
        return
      }
      // No live content — fall back to local cache if we have one.
      const cached = loadCachedContent()
      if (cached) {
        const usable = cached.is_paired && cached.is_active
        setState(prev => ({
          ...prev,
          content:     usable ? cached : null,
          isPaired:    cached.is_paired,
          isActive:    cached.is_active,
          pairingCode: cached.pairing_code ?? null,
          orientationType: cached.orientation_type,
        rotationDegrees: cached.rotation_degrees,
          isOnline:    false,
          noInternet:  offline,
          error:       null,
          loading:     false,
        }))
        return
      }
      setState(prev => ({
        ...prev,
        error:      offline ? 'No internet connection' : (err as Error)?.message || 'Cannot reach server',
        noInternet: offline,
        isOnline:   false,
        loading:    false,
      }))
      recordPlayerApiError(token, err, 'content fetch')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Silent refresh (no spinner, stages into pendingContent) ──────────────

  // `immediate` forces the fetched content onto the screen now instead of
  // staging it for the next item boundary. Server-pushed changes (content_update
  // from a publish) pass true so a republish always lands on the FIRST push —
  // it no longer depends on the separate `content_preparing` frame having set
  // the `publishing` flag in time (that ordering isn't guaranteed, and a staged
  // update for a single-item screen may never reach an item boundary). The 30s
  // background poll leaves it false so a routine re-fetch doesn't cut a playing
  // video mid-item.
  const fetchContentSilently = useCallback(async (immediate = false) => {
    const token = stateRef.current.deviceToken
    if (!token) return
    try {
      const content = await deviceApi.getContent(token)
      if (wasEvictedSinceLastFetch(content.cache_evict_at)) {
        clearCachedContent()
      }
      saveCachedContent(content)
      warmVideoCache(content)
      seedLicenseFromResponse(content)
      const fetchedAt   = Date.now()
      const wasActive   = stateRef.current.isActive
      const usable      = content.is_paired && content.is_active ? content : null
      const currentHasItems = hasRenderableContent(stateRef.current.content)
      setState(prev => {
        // Apply immediately (no item-boundary stage) when any of:
        //   - the caller forced it (a server-pushed publish/change)
        //   - nothing is currently playing ("No content assigned" / empty)
        //   - a publish is in flight (operator hit Publish — they want the
        //     new content NOW, not when the current video happens to end;
        //     the PublishingLoader covers the gap)
        // Otherwise stage into pendingContent so the active video / image
        // can finish its slot before the silent swap.
        const applyNow = immediate || prev.content === null || !currentHasItems || prev.publishing
        if (applyNow) {
          return {
            ...prev,
            screenId:       content.screen_id,
            screenName:     content.screen_name ?? '',
            branchName:     content.branch_name ?? '',
            content:        usable,
            pendingContent: null,
            isPaired:       content.is_paired,
            isActive:       content.is_active,
            pairingCode:    content.pairing_code ?? null,
            orientationType: content.orientation_type,
            rotationDegrees: content.rotation_degrees,
            isOnline:       true,
            loading:        false,   // clears the relink-transition loader
            publishing:     false,   // new content is on screen — loader off
            cacheExpired:   false,   // fresh fetch resets the TTL clock
            lastFetchedAt:  fetchedAt,
          }
        }
        return {
          ...prev,
          screenId:       content.screen_id,
          screenName:     content.screen_name ?? '',
          branchName:     content.branch_name ?? '',
          pendingContent: usable,
          isPaired:       content.is_paired,
          isActive:       content.is_active,
          pairingCode:    content.pairing_code ?? null,
          orientationType: content.orientation_type,
          rotationDegrees: content.rotation_degrees,
          isOnline:       true,
          cacheExpired:   false,
          lastFetchedAt:  fetchedAt,
        }
      })
      if (wasActive && !content.is_active) startDeactivatedPoll()
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // License-expiry 404s carry X-Disconnect-Code (→ err.disconnectCode);
        // a bare removal/relink 404 has none, so show the generic "removed" code.
        const disconnectCode = err.disconnectCode ?? DISCONNECT_CODES.REMOVED
        // See note in fetchContentImpl — same orphaned-device handling
        // (covers admin delete, relink, AND server-side license unpair).
        handleOrphanedAndReregister(disconnectCode)
        return
      }
      // Network error — keep what's showing, just flip the indicator.
      // Also clear loading in case a relink-transition flipped it on (see
      // WS handler) and the follow-up fetch is what failed; otherwise the
      // loader would stick forever.
      recordPlayerApiError(token, err, 'silent refresh')
      setState(prev => ({ ...prev, isOnline: false, loading: false }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── WebSocket (live content push) ────────────────────────────────────────

  function connectWebSocket() {
    if (wsRef.current) return
    // Identify the socket with our device token so the server delivers content
    // and command events scoped to this screen (instead of a global fan-out).
    // Falls back gracefully: if the token is missing/unknown the socket is
    // anonymous and content still arrives via the 30s refresh poll.
    const token = stateRef.current.deviceToken
    const wsUrl = BASE_URL.replace(/^http/, 'ws') + '/ws'
      + (token ? `?token=${encodeURIComponent(token)}` : '')
    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl)
    } catch {
      scheduleWsReconnect()
      return
    }
    wsRef.current = ws
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data)
        const commandId = typeof msg?.data?.command_id === 'string' ? msg.data.command_id : null
        const acknowledge = (command: string, status: 'received' | 'completed' | 'failed') => {
          if (!commandId || !stateRef.current.deviceToken) return Promise.resolve()
          return deviceApi.reportCommandEvent(
            stateRef.current.deviceToken, commandId, command, status,
          ).then(() => undefined).catch(() => undefined)
        }
        if (msg?.event === 'ping_screen') {
          // Dashboard "Ping" action — flash a small identification card
          // on the TV for ~5s. Only fires when the targeted screen is us.
          const targetId = msg.data?.screen_id ?? -1
          const myId     = stateRef.current.screenId
          if (targetId === myId) {
            void acknowledge('ping_screen', 'received')
            const overlay = {
              screenId: targetId,
              name:     String(msg.data?.name ?? ''),
            }
            setState(prev => ({ ...prev, pingOverlay: overlay }))
            void acknowledge('ping_screen', 'completed')
            // Auto-hide after 5s. Re-pinging within the window resets via
            // the next setState above.
            window.setTimeout(() => {
              if (stateRef.current.pingOverlay === overlay) {
                setState(prev => ({ ...prev, pingOverlay: null }))
              }
            }, 5_000)
          }
          return
        }
        if (msg?.event === 'restart_app') {
          // Dashboard "Restart app" command. For the web player a full page
          // reload IS the app restart — fresh JS, fresh state, re-register
          // from persisted creds. take_screenshot is deliberately not
          // handled here: browsers can't reliably capture their own
          // rendered output (cross-origin video taints the canvas), so the
          // dashboard shows N/A for web players instead.
          const targetId = msg.data?.screen_id ?? -1
          if (targetId === stateRef.current.screenId) {
            void acknowledge('restart_app', 'received')
              .then(() => acknowledge('restart_app', 'completed'))
              .finally(() => window.location.reload())
          }
          return
        }
        if (msg?.event === 'device_orphaned') {
          // Targeted push from the relink endpoint: the old player whose
          // device_token just got moved onto a different Screen row needs
          // to drop creds and re-register *now*, not on the next 30s poll
          // or the next content_update (which it may miss if the WS is
          // mid-reconnect). Match by token, not screen_id, because the
          // screen_id this player still holds locally is the relinked row
          // — which still exists and now belongs to a different device.
          if (msg.data?.device_token === stateRef.current.deviceToken) {
            // Prefer the code the server sent; fall back to its reason, else
            // treat an untagged orphan (relink / admin unpair) as "removed".
            const code = typeof msg.data?.code === 'number'
              ? msg.data.code
              : msg.data?.reason
                ? codeForReason(msg.data.reason)
                : DISCONNECT_CODES.REMOVED
            handleOrphanedAndReregister(code)
          }
          return
        }
        if (msg?.event === 'cache_evict') {
          // Admin hit Evict for our screen_id. Drop cache + creds-untouched
          // state immediately so the TV flips to the offline-reconnecting
          // view. The next /content fetch will restore everything if the
          // device can reach us again. License expiry no longer routes
          // here — it pushes `device_orphaned` instead (server-side unpair
          // already happened).
          const targetId = msg.data?.screen_id ?? -1
          const myId     = stateRef.current.screenId
          if (targetId === myId) {
            clearCachedContent()
            setState(prev => ({
              ...prev,
              content:        null,
              pendingContent: null,
              cacheExpired:   true,
            }))
          }
          return
        }
        if (msg?.event === 'license_renewed') {
          // Admin extended / cleared the license expiry. Re-seed the
          // monotonic countdown immediately so the player doesn't have to
          // wait for the next 30s heartbeat to learn about the new date.
          // No content fetch needed — the player was already paired and
          // playing; only the countdown changes.
          const targetId = msg.data?.screen_id ?? -1
          const myId     = stateRef.current.screenId
          if (targetId === myId) {
            seedLicenseFromResponse({
              disconnect_at:          msg.data?.disconnect_at ?? null,
              hours_until_disconnect: msg.data?.hours_until_disconnect ?? null,
            })
          }
          return
        }
        if (msg?.event === 'content_preparing') {
          // Operator hit Publish in the dashboard. Paint the brand loader
          // over whatever is currently showing so they see immediate
          // feedback instead of a black gap while the new content
          // downloads. fetchContentSilently below applies the new content
          // immediately (no item-boundary stage) when publishing is true,
          // and clears the flag in the same setState.
          //
          // Only for the CONTENT (visual) channel — an audio-only or
          // stop-screen-only publish has nothing new to show visually, so
          // covering the still-unchanged video/image with this loader read
          // as "content restarts" on every audio publish/schedule even
          // though the video never actually stopped. `channel` defaults to
          // 'content' server-side for any push that predates this field.
          //
          // 10s safety: if the follow-up content_update / fetch never
          // arrives (network drop mid-publish), clear publishing so the
          // loader doesn't hang forever — the previous content stays
          // visible underneath.
          const targetId = msg.data?.screen_id ?? -1
          const myId     = stateRef.current.screenId
          const isContentChannel = (msg.data?.channel ?? 'content') === 'content'
          if (isContentChannel && (targetId === -1 || myId === -1 || targetId === myId)) {
            setState(prev => ({ ...prev, publishing: true }))
            window.setTimeout(() => {
              if (stateRef.current.publishing) {
                setState(prev => ({ ...prev, publishing: false }))
              }
            }, 10_000)
          }
          return
        }
        if (msg?.event === 'content_update') {
          const targetId = msg.data?.screen_id ?? -1
          const myId     = stateRef.current.screenId
          const isContentChannel = (msg.data?.channel ?? 'content') === 'content'
          // -1 means "broadcast to everyone"; otherwise match by id.
          if (targetId === -1 || myId === -1 || targetId === myId) {
            const isResync = msg.data?.command === 'resync'
            if (isResync) void acknowledge('resync', 'received')
            // If we're currently unpaired and the push targets us, the
            // most likely cause is a relink. Flip to a loader immediately
            // so the pairing code vanishes the moment the dashboard
            // confirms — the next /content fetch will replace it with the
            // assigned content. Without this the pairing code stays on
            // screen for the round-trip of fetchContentSilently.
            if (!stateRef.current.isPaired) {
              setState(prev => ({
                ...prev,
                loading:     true,
                pairingCode: null,
                content:     null,
                error:       null,
              }))
            } else if (isContentChannel && !stateRef.current.publishing) {
              // The content_preparing event normally lands first and flips
              // `publishing` so fetchContentSilently applies the new content
              // immediately (vs. staging it for the next item boundary). If
              // we missed `content_preparing` — Firefox dropped the frame
              // during a WS reconnect, the events arrived out of order, or
              // an operator hit Publish from another tab — set the flag here
              // so the operator still gets the brand loader + an instant
              // swap instead of waiting up to a full video duration.
              setState(prev => ({ ...prev, publishing: true }))
              window.setTimeout(() => {
                if (stateRef.current.publishing) {
                  setState(prev => ({ ...prev, publishing: false }))
                }
              }, 10_000)
            }
            // Force the new content on immediately: a publish is an explicit
            // operator action, so it must land on this first push rather than
            // being staged for an item boundary that a single-item screen may
            // never reach (the root of "had to publish twice"). Applying an
            // audio/stop-only change "immediately" is harmless — the CONTENT
            // items are unchanged, so the visual carousel doesn't move; only
            // the audio bed / stop-screen manifest actually swap in.
            void fetchContentSilently(true).then(() => {
              if (isResync) void acknowledge('resync', stateRef.current.isOnline ? 'completed' : 'failed')
            })
          }
        }
      } catch { /* malformed frame — ignore */ }
    }
    ws.onclose = () => {
      wsRef.current = null
      scheduleWsReconnect()
    }
    ws.onerror = () => {
      // onerror is followed by onclose in the browser spec — let onclose
      // handle the reconnect to avoid double-scheduling.
    }
  }

  // ── Polling loops (unpaired / deactivated / refresh) ─────────────────────

  function startPairingPoll() {
    if (pairingPollTimerRef.current !== null) return
    const tick = async () => {
      pairingPollTimerRef.current = null
      const s = stateRef.current
      if (s.isPaired || s.screenRemoved) {
        if (s.screenRemoved) return
        if (!s.isActive) startDeactivatedPoll()
        else { connectWebSocket(); startRefreshLoop() }
        return
      }
      try {
        const content = await deviceApi.getContent(s.deviceToken)
        const identityChanged = s.screenId > 0 && s.screenId !== content.screen_id
        seedLicenseFromResponse(content)
        setState(prev => ({
          ...prev,
          screenId:    content.screen_id,
          screenName:  content.screen_name ?? '',
          branchName:  content.branch_name ?? '',
          content:     content.is_paired ? content : null,
          isPaired:    content.is_paired,
          isActive:    content.is_active,
          pairingCode: content.pairing_code ?? null,
          orientationType: content.orientation_type,
        rotationDegrees: content.rotation_degrees,
          error:       null,
          isOnline:    true,
          loading:     false,
        }))
        if (identityChanged) disconnectWebSocket()
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
        // License-expiry 404s carry X-Disconnect-Code (→ err.disconnectCode);
        // a bare removal/relink 404 has none, so show the generic "removed" code.
        const disconnectCode = err.disconnectCode ?? DISCONNECT_CODES.REMOVED
          // See note in fetchContentImpl — covers orphan / relink / license unpair.
          handleOrphanedAndReregister()
          return
        }
        setState(prev => ({ ...prev, isOnline: false }))
      }
      pairingPollTimerRef.current = window.setTimeout(tick, POLL_PAIRING_MS)
    }
    pairingPollTimerRef.current = window.setTimeout(tick, POLL_PAIRING_MS)
  }

  function startDeactivatedPoll() {
    if (deactivatedPollTimerRef.current !== null) return
    const tick = async () => {
      deactivatedPollTimerRef.current = null
      const s = stateRef.current
      if (s.isActive || s.screenRemoved) {
        if (!s.screenRemoved) { connectWebSocket(); startRefreshLoop() }
        return
      }
      try {
        const content = await deviceApi.getContent(s.deviceToken)
        seedLicenseFromResponse(content)
        setState(prev => ({
          ...prev,
          content:     (content.is_paired && content.is_active) ? content : null,
          isActive:    content.is_active,
          orientationType: content.orientation_type,
        rotationDegrees: content.rotation_degrees,
          error:       null,
          isOnline:    true,
        }))
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
        // License-expiry 404s carry X-Disconnect-Code (→ err.disconnectCode);
        // a bare removal/relink 404 has none, so show the generic "removed" code.
        const disconnectCode = err.disconnectCode ?? DISCONNECT_CODES.REMOVED
          // See note in fetchContentImpl — covers orphan / relink / license unpair.
          handleOrphanedAndReregister()
          return
        }
        setState(prev => ({ ...prev, isOnline: false }))
      }
      deactivatedPollTimerRef.current = window.setTimeout(tick, POLL_DEACTIVATED_MS)
    }
    deactivatedPollTimerRef.current = window.setTimeout(tick, POLL_DEACTIVATED_MS)
  }

  function startRefreshLoop() {
    if (refreshIntervalRef.current !== null) return
    refreshIntervalRef.current = window.setInterval(() => {
      const s = stateRef.current
      // Run the silent refresh whenever the player is in the steady "paired
      // + active" state — drop the previous `content !== null` guard, so a
      // transient `content=null` (e.g. an in-flight publish that cleared the
      // screen) still recovers without waiting for a manual reload. The
      // dedicated pairing/deactivated polls cover the other states.
      if (s.isPaired && s.isActive && !s.screenRemoved) {
        void fetchContentSilently()
      }
    }, REFRESH_INTERVAL_MS)
  }

  // ── Visibility watchdog ──────────────────────────────────────────────────
  // Browsers throttle (and sometimes freeze) timers + WebSockets in
  // background tabs. When the tab returns to the foreground we force-close
  // any stale WS so it reconnects, and immediately refetch content + a
  // heartbeat so dashboard changes made while the tab was hidden land
  // without waiting for the next 30s tick. This is the primary recovery
  // path for the "screen went blank, had to reload" symptom.
  const onVisibilityChange = useCallback(() => {
    if (typeof document === 'undefined') return
    if (document.visibilityState !== 'visible') return
    const s = stateRef.current
    if (!s.deviceToken) return
    // If we still have an open WS, the readyState tells us whether it
    // survived the freeze. Anything other than OPEN means it's dead in
    // practice — bin it and let scheduleWsReconnect rebuild from scratch.
    const ws = wsRef.current
    if (ws && ws.readyState !== WebSocket.OPEN) {
      try { ws.close() } catch { /* ignore */ }
      wsRef.current = null
    }
    if (!wsRef.current && s.isPaired && s.isActive && !s.screenRemoved) {
      connectWebSocket()
    }
    // Force an immediate content refetch so any change the operator made
    // while we were hidden lands now, not 30s from now.
    if (s.isPaired && s.isActive && !s.screenRemoved) {
      void fetchContentSilently()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Cache TTL eviction (every 60s) ───────────────────────────────────────
  // When the player has been replaying cached content longer than the
  // effective TTL (per-screen override OR CACHE_TTL_DEFAULT_HOURS), drop
  // the cache and show CacheExpiredScreen. Survives reloads because
  // saveCachedContent also persists `lastFetchedAt` to localStorage.
  // The fetchContent / fetchContentSilently paths reset this on success,
  // so the loader only ever shows for screens that are truly offline.

  function startCacheTtlLoop() {
    if (ttlCheckIntervalRef.current !== null) return
    const check = () => {
      const s = stateRef.current
      if (s.cacheExpired) return                       // already expired — nothing to do
      if (!hasRenderableContent(s.content)) return     // nothing to expire
      const lastFetched = s.lastFetchedAt ?? loadLastFetchedAt()
      if (lastFetched === null) return                 // first fetch hasn't landed yet
      const age = Date.now() - lastFetched
      if (age < effectiveCacheTtlMs(s.content)) return // still within TTL
      // TTL exceeded — evict so the operator sees the offline screen
      // instead of indefinitely-stale content. Cache is gone from storage
      // too; next successful /content fetch restores everything.
      clearCachedContent()
      setState(prev => ({
        ...prev,
        content:        null,
        pendingContent: null,
        cacheExpired:   true,
      }))
    }
    ttlCheckIntervalRef.current = window.setInterval(check, CACHE_TTL_CHECK_MS)
    check()   // also evaluate immediately so a stale boot doesn't paint for a minute
  }

  // ── Embargo countdown loop (every 60s + boot-time check) ─────────────────
  // The monotonic decrement runs every minute. On boot we also run it
  // synchronously before painting so a long-off device with an expired
  // license never flashes cached content before evicting.

  function startCountdownLoop() {
    if (countdownIntervalRef.current !== null) return
    countdownIntervalRef.current = window.setInterval(tickLicenseCountdown, COUNTDOWN_TICK_MS)
    tickLicenseCountdown()  // boot-time check: catches expired-while-off
  }

  // ── Heartbeat (every 30s once registered) ────────────────────────────────

  function startHeartbeatLoop() {
    if (heartbeatIntervalRef.current !== null) return
    const send = async () => {
      const s = stateRef.current
      if (!s.deviceToken) return
      try {
        // RTT telemetry: each heartbeat carries the round-trip measured on
        // the PREVIOUS one (measuring the in-flight request from inside
        // itself is impossible). One-beat lag is fine for a dashboard stat.
        const battery = await getBatteryManager()
        const sentAt = performance.now()
        const resp = await deviceApi.heartbeat({
          device_token:   s.deviceToken,
          is_screen_live: typeof document !== 'undefined' ? document.visibilityState === 'visible' : null,
          device_model:   deviceInfo.model,
          os_version:     deviceInfo.osVersion,
          app_version:    deviceInfo.appVersion,
          timezone:       Intl.DateTimeFormat().resolvedOptions().timeZone || null,
          latency_ms:     heartbeatRttMsRef.current,
          battery_pct:      battery ? Math.round(battery.level * 100) : null,
          battery_charging: battery ? battery.charging : null,
        })
        heartbeatRttMsRef.current = Math.round(performance.now() - sentAt)
        // Re-seed the offline license countdown from the server's response.
        // Heartbeats are a defense-in-depth path for license updates — the
        // primary mechanism is the WS `license_renewed` push which lands
        // within one RTT. Heartbeat success carries the same fields so an
        // offline-then-online device that missed the WS frame still gets
        // the new countdown on its next ping.
        seedLicenseFromResponse(resp)
      } catch (err) {
        // 404 covers: device row deleted, dashboard relink, OR server-side
        // license unpair via _enforce_license_or_unpair. All resolve via
        // auto-register (which matches by device_uid and lands a fresh
        // pairing code from the same Screen row). 5xx / network drops
        // silently so the next heartbeat retries.
        if (err instanceof ApiError && err.status === 404) {
        // License-expiry 404s carry X-Disconnect-Code (→ err.disconnectCode);
        // a bare removal/relink 404 has none, so show the generic "removed" code.
        const disconnectCode = err.disconnectCode ?? DISCONNECT_CODES.REMOVED
          handleOrphanedAndReregister()
          return
        }
      }
    }
    heartbeatIntervalRef.current = window.setInterval(send, HEARTBEAT_INTERVAL_MS)
    void send()  // immediate first ping so the dashboard flips us online without delay
  }

  // ── Auto-register (first-ever boot) ──────────────────────────────────────

  const autoRegister = useCallback(async () => {
    if (registerInFlightRef.current) return
    const retryAt = loadRegisterRetryAt()
    if (retryAt !== null && retryAt > Date.now()) {
      scheduleAutoRegister(retryAt - Date.now())
      return
    }
    registerInFlightRef.current = true
    try {
      const resp = await deviceApi.register({
        name:         deviceInfo.name,
        device_model: deviceInfo.model,
        os_version:   deviceInfo.osVersion,
        app_version:  deviceInfo.appVersion,
        device_uid:   loadDeviceUid(),
        device_kind:  deviceInfo.deviceKind,
      })
      saveCredentials(resp.device_token, resp.screen_id)
      clearRegisterRetryAt()
      registerBackoffMsRef.current = REGISTER_RETRY_DEFAULT_MS
      setState(prev => ({
        ...prev,
        registered:  true,
        deviceToken: resp.device_token,
        screenId:    resp.screen_id,
      }))
      startHeartbeatLoop()
      startCacheTtlLoop()
      startCountdownLoop()
      await fetchContentImpl(resp.device_token)
    } catch (err) {
      const offline = !isOnlineHint()
      let retryMs = REGISTER_RETRY_DEFAULT_MS
      if (err instanceof ApiError && err.status === 429) {
        retryMs = err.retryAfterMs ?? registerBackoffMsRef.current
        registerBackoffMsRef.current = Math.min(registerBackoffMsRef.current * 2, REGISTER_RETRY_MAX_MS)
        saveRegisterRetryAt(Date.now() + retryMs)
        scheduleAutoRegister(retryMs)
      }
      setState(prev => ({
        ...prev,
        error:      offline
          ? 'No internet connection'
          : err instanceof ApiError && err.status === 429
            ? 'Registration is temporarily rate limited; retrying automatically.'
            : (err as Error)?.message || 'Cannot reach server',
        noInternet: offline,
        loading:    false,
      }))
    } finally {
      registerInFlightRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchContentImpl])

  // ── Public actions ───────────────────────────────────────────────────────

  const fetchContent = useCallback(() => {
    const t = stateRef.current.deviceToken
    if (t) void fetchContentImpl(t)
  }, [fetchContentImpl])

  const applyPendingContent = useCallback(() => {
    setState(prev => {
      if (!prev.pendingContent) return prev
      return { ...prev, content: prev.pendingContent, pendingContent: null }
    })
  }, [])

  const reportError = useCallback((msg: string | null) => {
    const t = stateRef.current.deviceToken
    if (!t) return
    // Mirror every content-tile failure into Sentry, not just the device log.
    // PdfDisplay separately captures the underlying exception (richer: real
    // pdf.js error type + stack); this is the catch-all so image / video /
    // iframe failures — which have no other Sentry path — stop being silent
    // too. A null msg means "clear the error state", so don't report it.
    if (msg) {
      try {
        Sentry.captureMessage(msg, { level: 'error', tags: { content_error: 'true' } })
      } catch { /* sentry not configured — fall through */ }
    }
    deviceApi.reportError(t, msg).catch(() => { /* best-effort */ })
  }, [])

  const reportRetry = useCallback((itemName: string | null) => {
    const t = stateRef.current.deviceToken
    if (!t) return
    deviceApi.reportRetry(t, itemName).catch(() => { /* best-effort */ })
  }, [])

  // ── Boot sequence ────────────────────────────────────────────────────────

  useEffect(() => {
    if (didBootRef.current) return
    didBootRef.current = true

    const token    = loadToken()
    const screenId = loadScreenId() ?? -1
    const cached   = loadCachedContent()

    // Brand requirement: each browser reload of an UNPAIRED player gets a
    // fresh pairing code — the same way installing the Android app fresh
    // does. Once paired (cached.is_paired === true), we keep the same
    // device_token so content survives reloads. Without this guard a
    // browser tab would remember its first code forever, which lets stale
    // codes leak between sessions.
    if (token && cached?.is_paired) {
      // Paired tab: paint cached content + fetch fresh.
      setState(prev => ({
        ...prev,
        registered:  true,
        deviceToken: token,
        screenId,
        screenName:  cached.screen_name ?? '',
        branchName:  cached.branch_name ?? '',
        content:     (cached.is_active && hasRenderableContent(cached)) ? cached : null,
        isPaired:    cached.is_paired,
        isActive:    cached.is_active,
        pairingCode: cached.pairing_code ?? null,
        orientationType: cached.orientation_type ?? 'horizontal',
        rotationDegrees: cached.rotation_degrees ?? 0,
        loading:     false,
      }))
      // Seed lastFetchedAt from prefs so the TTL loop has something to
      // compare against if the device is booting offline — without this,
      // a 7-day-old cached payload would paint indefinitely on a cold
      // boot until the first successful /content fetch.
      const persistedLastFetched = loadLastFetchedAt()
      if (persistedLastFetched !== null) {
        setState(prev => ({ ...prev, lastFetchedAt: persistedLastFetched }))
      }
      startHeartbeatLoop()
      startCacheTtlLoop()
      startCountdownLoop()
      void fetchContentImpl(token)
    } else {
      // Fresh boot OR unpaired stale boot — wipe creds and register anew so
      // the operator sees a fresh code. Keeps device_uid (which clearCreds
      // intentionally preserves) for future "rebind by uid" support.
      if (token) clearCredentials()
      void autoRegister()
    }

    // Browser online/offline events — flip isOnline so the WiFi badge reacts
    // before the next fetch resolves.
    const onOnline  = () => {
      setState(prev => ({ ...prev, isOnline: true,  noInternet: false }))
      // Network just came back — force the same recovery path the
      // visibility watchdog uses so a dropped WS gets rebuilt and any
      // missed content_update lands without waiting for the 30s tick.
      onVisibilityChange()
    }
    const onOffline = () => setState(prev => ({ ...prev, isOnline: false, noInternet: true  }))
    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      disconnectWebSocket()
      clearTimer(registerRetryTimerRef)
      clearTimer(pairingPollTimerRef)
      clearTimer(deactivatedPollTimerRef)
      clearTimer(refreshIntervalRef)
      clearTimer(heartbeatIntervalRef)
      clearTimer(ttlCheckIntervalRef)
      clearTimer(countdownIntervalRef)
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { state, fetchContent, applyPendingContent, reportError, reportRetry }
}
